import { Facebook, Globe, Instagram } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { trackKindredSocialLinkClick } from "@/lib/socialLinkTracking";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppSectionHeader,
  AppText,
} from "../../ui-foundation";
import { KINDRED_SOCIAL_LINKS, type SocialLinkItem } from "../../shared/support";

type KindredSocialLinksCardProps = {
  sourceScreen: string;
  helperText?: string;
  includeWebsite?: boolean;
};

function iconForLink(link: SocialLinkItem) {
  switch (link.platform) {
    case "facebook":
      return <Facebook size={16} />;
    case "instagram":
      return <Instagram size={16} />;
    case "website":
      return <Globe size={16} />;
    default:
      return null;
  }
}

export default function KindredSocialLinksCard({
  sourceScreen,
  helperText = "Keep social links secondary: visible when you want them, never in the way of community or support.",
  includeWebsite = false,
}: KindredSocialLinksCardProps) {
  const { user } = useAuth();
  const links = includeWebsite
    ? KINDRED_SOCIAL_LINKS
    : KINDRED_SOCIAL_LINKS.filter((link) => link.platform !== "website");

  const handleOpen = (link: SocialLinkItem) => {
    window.open(link.url, "_blank", "noopener,noreferrer");
    void trackKindredSocialLinkClick({
      userId: user?.id,
      sourceScreen,
      destinationPlatform: link.platform,
      destinationUrl: link.url,
    });
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppSectionHeader
          title="Follow Kindred"
          subtitle="Facebook and Instagram are there when you want updates outside the app."
        />
      </AppCardHeader>
      <AppCardBody gap="$md">
        <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
          {helperText}
        </AppText>
        <div className="grid gap-3 sm:grid-cols-2">
          {links.map((link) => (
            <AppButton
              key={link.platform}
              type="button"
              tone="outline"
              size="sm"
              width="100%"
              onPress={() => handleOpen(link)}
            >
              <span className="inline-flex items-center gap-2">
                {iconForLink(link)}
                <span>{link.label}</span>
              </span>
            </AppButton>
          ))}
        </div>
      </AppCardBody>
    </AppCard>
  );
}
